import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { composerBus } from "@/lib/composerBus";
import { useCreditPreflight } from "@/hooks/useCreditPreflight";
import { ComposerOutboxPanel } from "@/components/composer/panels/ComposerOutboxPanel";
import { ComposerOverlays } from "@/components/composer/ComposerOverlays";
import { useComposerPublicationActions } from "@/components/composer/useComposerPublicationActions";
import { type RevisionSaveState } from "@/components/composer/draft/draftCanvasTypes";
import { VoiceProfilePanel } from "@/components/composer/panels/VoiceProfilePanel";
import { AvatarsPanel } from "@/components/composer/panels/AvatarsPanel";
import { FrameworkPanel } from "@/components/composer/panels/FrameworkPanel";
import { createUserEditRevision, deleteDraft, reorderDrafts, saveUserEditRevision } from "@/lib/briefRevisions";

import { Skeleton } from "@/components/ui/skeleton";
import { useWorkspace } from "@/hooks/useWorkspace";

import {
  getLatestDraftForBrief,
  getLatestPlanForBrief,
  resolveBriefsWithReferences,
  type ContentDestinationFormat,
} from "@/lib/composerCore";
import { ComposerShell } from "@/components/composer/ComposerShell";
import { DraftCanvas, type DraftCanvasCommit } from "@/components/composer/draft/DraftCanvas";

import { PlanningProvider } from "@/components/composer/PlanningContext";
import { DraftsQueuePanel } from "@/components/composer/draft/DraftsQueuePanel";
import { CorpusDockPanel } from "@/components/composer/corpus/CorpusDockPanel";
import { ComposerLibraryPicker } from "@/components/composer/library/ComposerLibraryPicker";
import { ComposerExcerptsPicker } from "@/components/composer/library/ComposerExcerptsPicker";
import { type TemplateSelection } from "@/components/composer/templates/templateTypes";
import { SaveAsTemplateDialog } from "@/components/composer/templates/SaveAsTemplateDialog";
import { TemplateWizardDialog } from "@/components/composer/templates/TemplateWizardDialog";
import { TemplatesPanel } from "@/components/composer/builder/TemplatesPanel";
import { templateKind } from "@/lib/composerTemplates";
import { templateToBriefDocument } from "@/lib/composerPushDocs";
import { parseBriefDocument, type BriefDocument } from "@/lib/briefDocument";
import { PromptsPanel } from "@/components/composer/builder/PromptsPanel";
import { BlocksPanel } from "@/components/composer/builder/BlocksPanel";
import { BlueprintsPanel } from "@/components/composer/builder/BlueprintsPanel";
import { ArchivePanel } from "@/components/composer/builder/ArchivePanel";
import { BibliographyTabPanel } from "@/components/composer/bibliography/BibliographyTabPanel";
import { CitationsPreviewPanel } from "@/components/composer/CitationsPreviewPanel";
import { CitationsPreviewCollapsible } from "@/components/composer/CitationsPreviewCollapsible";

import { tierHasCapability } from "@/lib/tier";
import { AppSeo } from "@/components/AppSeo";

import {
  FRAMEWORK_COLUMN_CAP,
  countBy,
  copyPublicationText,
  downloadPublicationFile,
  type BriefDraftState,
  type PendingDispatch,
} from "@/components/composer/composerPageHelpers";
import { useComposerSnapshot } from "@/components/composer/useComposerSnapshot";
import { useCanvasBriefSelection } from "@/components/composer/useCanvasBriefSelection";
import { buildCorpusRefChipsFromSnapshot, setChipRegistry } from "@/lib/refMarkers";

const loadingFallback = (
  <div className="space-y-6">
    <Skeleton className="h-10 w-48" />
    <div className="grid gap-4 lg:grid-cols-3">
      {Array.from({ length: 3 }).map((_, index) => (
        <Skeleton key={index} className="h-40 w-full" />
      ))}
    </div>
  </div>
);

const Composer = () => {
  const { active } = useWorkspace();
  const { ensure: ensureCreditsForJob, dialog: creditPreflightDialog } = useCreditPreflight(active?.id ?? null);
  void ensureCreditsForJob; // reserved for revived briefs panel

  // Deep link from /composer/inbox (or anywhere): `?openPiece=<id>` opens that
  // Piece in the canvas. We resolve it after the Composer snapshot is loaded
  // below; emitting on the bus here can race the DraftCanvas subscription and
  // leave the chat rail with no active Piece.
  const [searchParams, setSearchParams] = useSearchParams();

  const {
    snapshot,
    setSnapshot,
    loading,
    refresh,
    refreshQuiet,
    voiceDraft,
    setVoiceDraft,
    savingVoice,
    handleSaveVoice,
    avatarDraft,
    setAvatarDraft,
    creatingAvatar,
    handleCreateAvatar,
    handleUpdateAvatar,
    handleDeleteAvatar,
    handleReorderAvatars,
  } = useComposerSnapshot(active?.id ?? null);

  const [pendingDispatch, setPendingDispatch] = useState<PendingDispatch | null>(null);
  const [drawerDispatchId, setDrawerDispatchId] = useState<string | null>(null);
  const [errorBriefId, setErrorBriefId] = useState<string | null>(null);
  const [snapshotViewer, setSnapshotViewer] = useState<{
    contextSnapshot: unknown;
    runLabel: string;
  } | null>(null);

  const [revisionSaveByDraft, setRevisionSaveByDraft] = useState<Record<string, { state: RevisionSaveState; error?: string }>>({});
  const [savingAsEditableByDraft, setSavingAsEditableByDraft] = useState<Record<string, boolean>>({});

  // Briefs panel is parked from the bottom dock (2026-05-05).
  const [briefDraft, setBriefDraft] = useState<BriefDraftState>({
    title: "",
    contentType: "",
    prompt: "",
    notes: "",
  });
  const [saveAsTemplateOpen, setSaveAsTemplateOpen] = useState(false);
  const [activeTemplate, setActiveTemplate] = useState<TemplateSelection | null>(null);
  const [wizardTemplate, setWizardTemplate] = useState<TemplateSelection | null>(null);

  /**
   * Template card primary action (and Preview dialog "Use") routes here.
   *
   *   Static  → push structure straight into the Draft Canvas (no AI, no chat).
   *   Dynamic → open the wizard intake dialog. On submit, switch the rail to
   *             Planning Agent and seed the chat with a pre-filled first user
   *             turn (template metadata + wizard answers). The Planning Agent
   *             then picks up its six-gather flow from there.
   */
  const handleTemplateSelected = (sel: TemplateSelection) => {
    setActiveTemplate(sel);
    const kind = templateKind({ wizard_questions: sel.wizardQuestions as never });
    if (kind === "static") {
      const doc = templateToBriefDocument({
        introMessage: sel.introMessage ?? null,
        defaultStructure: sel.defaultStructure,
      });
      composerBus.emit("draft:insert", {
        doc,
        mode: "replace-clean",
        source: sel.templateId ? `template:${sel.templateId}` : "template",
        label: sel.name,
      });
      toast.success(`Template loaded: ${sel.name}`);
      return;
    }
    // Dynamic: open the content-specific wizard. Do NOT touch Draft Canvas.
    if (!activeBriefForCanvas) {
      toast.message("Open or create a Piece first", {
        description: "Dynamic templates seed the Planning Agent on the active Piece.",
      });
      return;
    }
    setWizardTemplate(sel);
  };

  const handleWizardSubmit = (payload: import("@/components/composer/templates/TemplateWizardDialog").WizardSubmitPayload) => {
    if (!wizardTemplate || !activeBriefForCanvas) {
      setWizardTemplate(null);
      return;
    }
    const sel = wizardTemplate;
    const { answers, selectedAvatarName, intakeAttachments } = payload;
    const lines: string[] = [];
    lines.push(`I'd like to plan a **${sel.contentType || "piece"}** using the template **${sel.name}**.`);
    if (sel.introMessage) {
      lines.push("");
      lines.push(`> ${sel.introMessage}`);
    }
    lines.push("");
    lines.push(`**Audience avatar:** ${selectedAvatarName}`);
    const structure = Array.isArray(sel.defaultStructure) ? (sel.defaultStructure as Array<Record<string, unknown>>) : [];
    if (structure.length > 0) {
      lines.push("");
      lines.push("**Template outline:**");
      for (const s of structure) {
        const heading = typeof s.section === "string" ? s.section : null;
        if (heading) lines.push(`- ${heading}`);
      }
    }
    const filled = answers.filter((a) => a.answer.length > 0);
    if (filled.length > 0) {
      lines.push("");
      lines.push("**Wizard answers:**");
      for (const a of filled) {
        lines.push(`- _${a.question.prompt}_ — ${a.answer}`);
      }
    }
    if (intakeAttachments.length > 0) {
      lines.push("");
      lines.push("**Pinned references:**");
      for (const chip of intakeAttachments) {
        lines.push(`- [${chip.kind}] ${chip.label}${chip.usage ? ` — ${chip.usage}` : ""}`);
      }
    }
    lines.push("");
    lines.push("Please use these as the starting point and walk me through anything still missing for the brief.");
    const seedText = lines.join("\n");

    // Flip the rail to Planning Agent so the user sees the conversation kick off.
    composerBus.emit("rail:setMode", { mode: "planning" });
    // BriefIntakeChat (mounted via PlanningRailPanel) subscribes and will
    // auto-send this as the first user turn if its transcript is empty.
    composerBus.emit("planning:seed", {
      pieceId: activeBriefForCanvas.id,
      text: seedText,
      source: sel.templateId ? `template:${sel.templateId}` : "template",
      templateId: sel.templateId ?? undefined,
      templateName: sel.name,
    });
    setWizardTemplate(null);
  };



  const canDispatch = tierHasCapability(active?.account_tier ?? null, "content_publishing_dispatch");

  const activeFramework = useMemo(
    () => snapshot.frameworks.find((framework) => framework.is_active) ?? snapshot.frameworks[0] ?? null,
    [snapshot.frameworks],
  );
  const frameworkColumns = useMemo(
    () =>
      snapshot.columns
        .filter((column) => column.framework_id === activeFramework?.id)
        .sort((a, b) => a.position - b.position),
    [snapshot.columns, activeFramework?.id],
  );
  void useMemo(() => countBy(snapshot.principles, (item) => item.framework_column_id), [snapshot.principles]);
  void useMemo(() => countBy(snapshot.topics, (item) => item.framework_column_id), [snapshot.topics]);

  const briefsWithReferences = useMemo(() => resolveBriefsWithReferences(snapshot), [snapshot]);
  // Track `#plan=<planId>` from the URL so the Planning rail can switch
  // between multiple sessions per Piece (parity with AI Chat threads).
  const [activePlanId, setActivePlanId] = useState<string | null>(() => {
    if (typeof window === "undefined") return null;
    const raw = window.location.hash.startsWith("#") ? window.location.hash.slice(1) : window.location.hash;
    return new URLSearchParams(raw).get("plan");
  });
  useEffect(() => {
    const onHash = () => {
      const raw = window.location.hash.startsWith("#") ? window.location.hash.slice(1) : window.location.hash;
      setActivePlanId(new URLSearchParams(raw).get("plan"));
    };
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);
  const latestPlanByBriefId = useMemo(
    () =>
      new Map(
        briefsWithReferences.map((brief) => [
          brief.id,
          getLatestPlanForBrief(brief.id, snapshot.briefPlans, activePlanId),
        ]),
      ),
    [briefsWithReferences, snapshot.briefPlans, activePlanId],
  );

  const latestDraftByBriefId = useMemo(
    () => new Map(briefsWithReferences.map((brief) => [brief.id, getLatestDraftForBrief(brief.id, snapshot.briefDrafts)])),
    [briefsWithReferences, snapshot.briefDrafts],
  );

  const {
    setCanvasBriefIdOverride,
    setForceBlankCanvas,
    setActiveRevisionByBrief,
    activeBriefForCanvas,
    activeCanvasDraft,
  } = useCanvasBriefSelection({ snapshot, latestDraftByBriefId });

  // Keep the ref-marker chip registry in sync with the active piece's
  // intake_attachments so `[ref:kind:id]` decorations resolve to labels.
  useEffect(() => {
    if (!activeBriefForCanvas?.id) return;
    const meta = activeBriefForCanvas.metadata;
    const raw =
      meta && typeof meta === "object" && !Array.isArray(meta)
        ? (meta as Record<string, unknown>).intake_attachments
        : null;
    const chips = Array.isArray(raw)
      ? (raw as Array<Record<string, unknown>>).flatMap((r) => {
          const kind = r?.kind;
          const id = r?.id;
          const label = r?.label;
          if (typeof kind !== "string" || typeof id !== "string" || typeof label !== "string") return [];
          const usage = r?.usage;
          const safeUsage: "cite" | "consult" | "quote_verbatim" =
            usage === "cite" || usage === "quote_verbatim" ? usage : "consult";
          return [{
            kind: kind as import("@/lib/refMarkers").RefKind,
            id,
            label,
            usage: safeUsage,
          }];
        })
      : [];

    const corpusChips = buildCorpusRefChipsFromSnapshot(activeCanvasDraft?.context_snapshot);
    setChipRegistry(activeBriefForCanvas.id, [...chips, ...corpusChips]);
  }, [activeBriefForCanvas?.id, activeBriefForCanvas?.metadata, activeCanvasDraft?.context_snapshot]);

  useEffect(() => {
    const openId = searchParams.get("openPiece");
    if (!openId || loading) return;
    const target = snapshot.briefs.find((brief) => brief.id === openId);
    const next = new URLSearchParams(searchParams);
    next.delete("openPiece");
    setSearchParams(next, { replace: true });
    if (!target) return;
    setForceBlankCanvas(false);
    setCanvasBriefIdOverride(openId);
    setActiveRevisionByBrief((prev) => {
      if (!(openId in prev)) return prev;
      const copy = { ...prev };
      delete copy[openId];
      return copy;
    });
  }, [loading, searchParams, setSearchParams, setCanvasBriefIdOverride, setForceBlankCanvas, setActiveRevisionByBrief, snapshot.briefs]);

  // Auto-focus a newly generated AI draft in the Draft Canvas. Without this,
  // if the canvas was pinned to a user_edit revision (via "Save as Editable
  // Draft"), the fresh AI draft would land in the drafts list unselected and
  // appear to have "disappeared" from the canvas after streaming ended.
  useEffect(() => {
    return composerBus.subscribe("piece:draft:focus", ({ pieceId, draftId }) => {
      setForceBlankCanvas(false);
      setCanvasBriefIdOverride(pieceId);
      setActiveRevisionByBrief((prev) => ({ ...prev, [pieceId]: draftId }));
    });
  }, [setCanvasBriefIdOverride, setForceBlankCanvas, setActiveRevisionByBrief]);

  const handleDeleteDraft = async (draftId: string) => {
    const target = snapshot.briefDrafts.find((d) => d.id === draftId);
    if (!target) return;
    const pieceId = target.piece_id;
    try {
      await deleteDraft({ draftId });
      // Optimistic snapshot patch so the row disappears immediately.
      setSnapshot((prev) => ({
        ...prev,
        briefDrafts: prev.briefDrafts.filter((d) => d.id !== draftId),
      }));
      // If the deleted draft was the active canvas draft, pick the newest
      // remaining draft for the same piece; if none, blank the canvas.
      if (activeCanvasDraft?.id === draftId) {
        const remaining = snapshot.briefDrafts
          .filter((d) => d.id !== draftId && d.piece_id === pieceId)
          .sort((a, b) => {
            const aT = new Date(a.updated_at ?? a.created_at ?? 0).getTime();
            const bT = new Date(b.updated_at ?? b.created_at ?? 0).getTime();
            return bT - aT;
          });
        if (remaining.length > 0) {
          setActiveRevisionByBrief((prev) => ({ ...prev, [pieceId]: remaining[0].id }));
        } else {
          setActiveRevisionByBrief((prev) => {
            const next = { ...prev };
            delete next[pieceId];
            return next;
          });
          setCanvasBriefIdOverride(null);
          setForceBlankCanvas(true);
        }
      }
      toast.success("Draft deleted");
      void refreshQuiet();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to delete draft");
    }
  };

  const handleReorderDrafts = async (orderedDraftIds: string[]) => {
    // Optimistic reorder locally.
    const orderIndex = new Map(orderedDraftIds.map((id, i) => [id, i + 1]));
    setSnapshot((prev) => {
      const reordered = [...prev.briefDrafts].sort((a, b) => {
        const aOrder = orderIndex.get(a.id);
        const bOrder = orderIndex.get(b.id);
        if (aOrder !== undefined && bOrder !== undefined) return aOrder - bOrder;
        if (aOrder !== undefined) return -1;
        if (bOrder !== undefined) return 1;
        const aT = new Date(a.updated_at ?? a.created_at ?? 0).getTime();
        const bT = new Date(b.updated_at ?? b.created_at ?? 0).getTime();
        return bT - aT;
      });
      return { ...prev, briefDrafts: reordered };
    });
    try {
      await reorderDrafts(orderedDraftIds);
      void refreshQuiet();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save draft order");
      void refresh();
    }
  };

  const handleDraftCanvasPromoted = (commit?: DraftCanvasCommit) => {
    setForceBlankCanvas(false);
    if (commit) {
      setCanvasBriefIdOverride(commit.briefId);
      setActiveRevisionByBrief((prev) => ({ ...prev, [commit.briefId]: commit.draftId }));
      setRevisionSaveByDraft((prev) => ({ ...prev, [commit.draftId]: { state: "saved" } }));
      setSnapshot((prev) => {
        const now = new Date().toISOString();
        const committedDraft = {
          ...commit.draft,
          piece_id: commit.briefId,
          draft_doc: commit.doc,
          draft_title: commit.title === undefined ? commit.draft.draft_title : commit.title,
          updated_at: commit.draft.updated_at ?? now,
        };
        const exists = prev.briefDrafts.some((d) => d.id === commit.draftId);
        return {
          ...prev,
          briefDrafts: exists
            ? prev.briefDrafts.map((d) => (d.id === commit.draftId ? { ...d, ...committedDraft } : d))
            : [committedDraft, ...prev.briefDrafts],
        };
      });
      void refreshQuiet();
      return;
    }
    void refresh();
  };


  const {
    dispatchingPubId,
    resendingDispatchId,
    handleDispatchPublication,
    handleResendToFailed,
    handleArchivePublication,
    handlePromotePublication,
    handleSendFromOutbox,
  } = useComposerPublicationActions({
    workspaceId: active?.id ?? null,
    snapshot,
    setSnapshot,
    refresh,
    setPendingDispatch,
    setDrawerDispatchId,
  });

  if (!active || loading) {
    return loadingFallback;
  }

  const frameworkPanel = (
    <FrameworkPanel
      workspaceId={active.id}
      workspaceTier={active?.account_tier ?? null}
      framework={activeFramework}
      columns={frameworkColumns}
      principles={snapshot.principles}
      topics={snapshot.topics}
      onChange={refresh}
    />
  );

  const voicePanel = (
    <VoiceProfilePanel
      voiceDraft={voiceDraft}
      setVoiceDraft={setVoiceDraft}
      savingVoice={savingVoice}
      onSave={() => void handleSaveVoice()}
    />
  );

  const avatarsPanel = (
    <AvatarsPanel
      avatars={snapshot.avatars}
      avatarDraft={avatarDraft}
      setAvatarDraft={setAvatarDraft}
      creatingAvatar={creatingAvatar}
      onCreate={() => void handleCreateAvatar()}
      voiceModeALabel={voiceDraft.voice_mode_a_label || snapshot.voiceProfile?.voice_mode_a_label || "Primary Voice"}
      voiceModeBLabel={voiceDraft.voice_mode_b_label || snapshot.voiceProfile?.voice_mode_b_label || "Teaching"}
      dualVoiceModesEnabled={voiceDraft.dual_voice_modes_enabled}
      onEditAvatar={handleUpdateAvatar}
      onDeleteAvatar={handleDeleteAvatar}
      onReorderAvatars={handleReorderAvatars}
    />
  );


  const outboxPanel = (
    <ComposerOutboxPanel
      briefs={snapshot.briefs}
      drafts={snapshot.briefDrafts}
      publications={snapshot.briefPublications}
      connectors={snapshot.publishingConnectors}
      canDispatch={canDispatch}
      onPromote={(pub, bucket) => void handlePromotePublication(pub, bucket)}
      onSend={(pub, connectorId, options) => handleSendFromOutbox(pub, connectorId, options)}
      onArchive={(pub) => void handleArchivePublication(pub)}
      onCopy={(pub) => void copyPublicationText(pub.prepared_body ?? "")}
      onDownload={(pub) =>
        downloadPublicationFile(
          pub.prepared_body ?? "",
          pub.prepared_title || snapshot.briefs.find((b) => b.id === pub.piece_id)?.title || "publication",
          pub.destination_format as ContentDestinationFormat,
        )
      }
    />
  );

  return (
    <>
      <AppSeo title="Composer" />
      {creditPreflightDialog}
      <PlanningProvider
        workspaceId={active?.id ?? null}
        brief={activeBriefForCanvas}
        latestPlan={activeBriefForCanvas ? latestPlanByBriefId.get(activeBriefForCanvas.id) ?? null : null}
        onRefresh={refresh}
      >
      <ComposerShell
        workspaceLabel={active?.workspace_label ?? active?.display_name ?? undefined}
        workspaceId={active?.id}
        topBarActions={null}
        bottomDockPanels={{
          codex: { framework: frameworkPanel, voice: voicePanel, avatar: avatarsPanel },
          builder: {
            blocks: <BlocksPanel mode="catalog" />,
            blueprints: <BlueprintsPanel mode="catalog" />,
          },
          vault: {
            templates: (
              <TemplatesPanel
                onSelect={handleTemplateSelected}
                onSaveCurrent={() => setSaveAsTemplateOpen(true)}
                canSaveCurrent={Boolean(briefDraft.title.trim())}
                getCurrentDoc={() => {
                  const body = activeCanvasDraft?.draft_body;
                  if (!body) return null;
                  return parseBriefDocument(body);
                }}
              />
            ),
            prompts: <PromptsPanel />,
            archive: <ArchivePanel workspaceId={active.id} />,
          },
          corpus: <CorpusDockPanel workspaceId={active?.id} />,
        }}
        draftsPanel={
          <div className="h-full p-3">
            <DraftsQueuePanel
              briefs={snapshot.briefs}
              drafts={snapshot.briefDrafts}
              activeDraftId={activeCanvasDraft?.id ?? null}
              onSelect={(briefId, draftId) => {
                setForceBlankCanvas(false);
                setCanvasBriefIdOverride(briefId);
                setActiveRevisionByBrief((prev) => ({ ...prev, [briefId]: draftId }));
              }}
              onDelete={handleDeleteDraft}
              onReorder={handleReorderDrafts}
            />
          </div>
        }
        outboxPanel={outboxPanel}
        libraryPanel={<ComposerLibraryPicker workspaceId={active?.id} />}
        excerptsPanel={<ComposerExcerptsPicker workspaceId={active?.id} />}
        bibliographyPanel={
          <BibliographyTabPanel
            workspaceId={active?.id}
            pieceId={activeBriefForCanvas?.id}
            onChanged={refresh}
          />
        }
        bottomDockCounts={{
          codex: {
            framework: { current: frameworkColumns.length, max: FRAMEWORK_COLUMN_CAP },
            voice: { current: snapshot.voiceProfile ? 1 : 0 },
            avatar: { current: snapshot.avatars.length },
          },
        }}
      >
        <div className="flex h-full min-h-0 flex-col gap-4">
          <section
            aria-label="Draft canvas"
            className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-border bg-card p-4"
          >
            <DraftCanvas
              workspaceId={active?.id ?? null}
              brief={activeBriefForCanvas}
              latestDraft={activeCanvasDraft}
              saveState={activeCanvasDraft ? revisionSaveByDraft[activeCanvasDraft.id]?.state : undefined}
              saveError={activeCanvasDraft ? revisionSaveByDraft[activeCanvasDraft.id]?.error : null}
              onTitleSaved={() => { void refresh(); }}
              contextExtras={{ notifyChanged: refreshQuiet }}
              onChange={async (doc) => {
                if (!activeCanvasDraft || activeCanvasDraft.revision_kind !== "user_edit") return;
                setRevisionSaveByDraft((prev) => ({ ...prev, [activeCanvasDraft.id]: { state: "saving" } }));
                try {
                  await saveUserEditRevision({ draftId: activeCanvasDraft.id, doc });
                  setRevisionSaveByDraft((prev) => ({ ...prev, [activeCanvasDraft.id]: { state: "saved" } }));
                  // Patch the active draft locally so Citations Preview sees
                  // updated footnote/endnote attrs (refId, locator) without a
                  // full snapshot reload — that reload was causing the
                  // double-load skeleton flash on every autosave.
                  setSnapshot((prev) => ({
                    ...prev,
                    briefDrafts: prev.briefDrafts.map((d) =>
                      d.id === activeCanvasDraft.id ? { ...d, draft_doc: doc, updated_at: new Date().toISOString() } : d,
                    ),
                  }));
                } catch (err) {
                  setRevisionSaveByDraft((prev) => ({
                    ...prev,
                    [activeCanvasDraft.id]: { state: "error", error: err instanceof Error ? err.message : "Save failed" },
                  }));
                }
              }}
              onDraftPromoted={handleDraftCanvasPromoted}
              onApprovedBriefCleared={() => { void refresh(); }}
              onNewDraft={() => {
                setCanvasBriefIdOverride(null);
                setForceBlankCanvas(true);
              }}
              savingAsEditable={
                activeCanvasDraft ? savingAsEditableByDraft[activeCanvasDraft.id] === true : false
              }
              onSaveAsEditable={async () => {
                if (!active?.id || !activeBriefForCanvas || !activeCanvasDraft) return;
                if (activeCanvasDraft.revision_kind === "user_edit") return;
                setSavingAsEditableByDraft((prev) => ({ ...prev, [activeCanvasDraft.id]: true }));
                try {
                  const rev = await createUserEditRevision({
                    workspaceId: active.id,
                    briefId: activeBriefForCanvas.id,
                    source: activeCanvasDraft,
                  });
                  await refresh();
                  setActiveRevisionByBrief((prev) => ({
                    ...prev,
                    [activeBriefForCanvas.id]: rev.id,
                  }));
                  toast.success("Saved as editable draft");
                } catch (err) {
                  toast.error(err instanceof Error ? err.message : "Couldn't save as editable draft");
                } finally {
                  setSavingAsEditableByDraft((prev) => {
                    const next = { ...prev };
                    delete next[activeCanvasDraft.id];
                    return next;
                  });
                }
              }}
              onDeleteDraft={handleDeleteDraft}
            />
          </section>

          {(() => {
            if (!active?.id || !activeBriefForCanvas) return null;
            const pieceWithRefs = briefsWithReferences.find((b) => b.id === activeBriefForCanvas.id);
            if (!pieceWithRefs) return null;
            const draftDoc =
              (activeCanvasDraft?.draft_doc as BriefDocument | null | undefined) ??
              parseBriefDocument(activeCanvasDraft?.draft_body ?? null);
            return (
              <CitationsPreviewCollapsible
                workspaceId={active.id}
                hasCitations={(pieceWithRefs.references ?? []).some((r) => r.reference_type === "source")}
              >
                <CitationsPreviewPanel
                  workspaceId={active.id}
                  piece={pieceWithRefs}
                  draftDoc={draftDoc}
                  onChanged={refresh}
                />
              </CitationsPreviewCollapsible>
            );
          })()}

          {loading ? loadingFallback : null}

          <ComposerOverlays
            workspaceId={active?.id ?? null}
            snapshot={snapshot}
            refresh={refresh}
            canDispatch={canDispatch}
            drawerDispatchId={drawerDispatchId}
            setDrawerDispatchId={setDrawerDispatchId}
            resendingDispatchId={resendingDispatchId}
            onResendToFailed={handleResendToFailed}
            errorBriefId={errorBriefId}
            setErrorBriefId={setErrorBriefId}
            latestPlanByBriefId={latestPlanByBriefId}
            latestDraftByBriefId={latestDraftByBriefId}
            snapshotViewer={snapshotViewer}
            setSnapshotViewer={setSnapshotViewer}
            pendingDispatch={pendingDispatch}
            setPendingDispatch={setPendingDispatch}
            dispatchingPubId={dispatchingPubId}
            onConfirmDispatch={(publicationId) => void handleDispatchPublication(publicationId)}
          />
          {/* Templates and My Templates now live inside the Builder bottom-dock tab. */}
          <SaveAsTemplateDialog
            open={saveAsTemplateOpen}
            onOpenChange={setSaveAsTemplateOpen}
            defaults={{
              name: briefDraft.title,
              contentType: briefDraft.contentType || "other",
              introMessage: activeTemplate?.introMessage ?? undefined,
              wizardQuestions: activeTemplate?.wizardQuestions as never,
              defaultStructure: activeTemplate?.defaultStructure as never,
            }}
          />
          <TemplateWizardDialog
            open={Boolean(wizardTemplate)}
            onOpenChange={(o) => { if (!o) setWizardTemplate(null); }}
            templateName={wizardTemplate?.name ?? ""}
            introMessage={wizardTemplate?.introMessage ?? null}
            wizardQuestions={wizardTemplate?.wizardQuestions as never}
            defaultStructure={wizardTemplate?.defaultStructure as never}
            pieceId={activeBriefForCanvas?.id ?? null}
            templateId={wizardTemplate?.templateId ?? null}
            onSubmit={handleWizardSubmit}
          />
        </div>
      </ComposerShell>
      </PlanningProvider>
    </>
  );
};

export default Composer;
